import { useTranslation } from "next-i18next";
import { colors } from "../../../styles/colors";
import { Block } from "../../../styles/basicStyles";
import { FC, useEffect, useState } from "react";
import { GraphColumn, GraphColumnTitle, GraphContent, GraphLine, GraphRotateText, GraphLabel } from "./styles";
import { waitForMs } from "../../../utils/generic";
import { AnyObject } from "../../../models/generic";
import VisibilitySensor from "react-visibility-sensor";

const anim: AnyObject = {
  u: 0,
  duration: 0,
  tZero: Date.now(),
  isAnimating: true,
  columnPaused: [false, false, false, false],

  start: function (duration: number) {
    this.isAnimating = true;
    this.duration = duration;
    this.u = 0;
    this.tZero = Date.now();
    this.columnPaused = [false, false, false, false];

    requestAnimationFrame(() => this.run());
  },

  run: function () {
    if(!this.isAnimating) return;

    this.u += (Date.now() - this.tZero) / this.duration;
    this.tZero = Date.now();

    if(this.u < 1) {
      requestAnimationFrame(() => this.run());
    } 
    else {
      this.u = 1;
      this.onFinish();
    }

    dot.move(this.u);
  },

  onFinish: function () {
    setTimeout(() => {
      dot.init('dot', 'curve');
      dot.move(0);

      selected.init();
      this.start(7000);
    }, 1000);
  },

  pause() {
    this.isAnimating = false;
  },

  resume() {
    this.tZero = Date.now();
    this.isAnimating = true;
    requestAnimationFrame(() => this.run());
  }
};

const dot: AnyObject = {
  sprite: null,
  track: null,
  column: null,

  init: function (sprite: string, track: string) {
    this.sprite = document.getElementById(sprite);
    this.track = document.getElementById(track);
    this.column = document.getElementById('column1')?.getBoundingClientRect();
  },

  move: async function (u: number) {
    if(this.sprite) {
      const spriteDimensions = this.sprite?.getBoundingClientRect();
      const spritePosition = spriteDimensions.left + (spriteDimensions.width / 2) - (this.column?.left || 0);

      const p = this.track.getPointAtLength(u * this.track.getTotalLength());
      this.sprite.setAttribute("transform", `translate(${p.x}, ${p.y})`);

      // Remove all "active" styles
      selected.remove();

      // Show the "active" bg color
      if(spritePosition <= this.column?.width) selected.activeBg(0);
      else if(spritePosition > this.column?.width && spritePosition <= (2 * this.column?.width)) selected.activeBg(1);
      else if(spritePosition > (2 * this.column?.width) && spritePosition <= (3 * this.column?.width)) selected.activeBg(2);
      else selected.activeBg(3);
      
      // Stop and resume when reaching the middle of each block
      for(let i = 0; i < 4; i++) {
        const factor = (i + 1) - 0.5;

        if(!anim.columnPaused[i] && spritePosition >= (this.column?.width * factor)) {
          anim.columnPaused[i] = true;

          anim.pause();
          selected.activeTitle(i);

          await waitForMs(2000);

          anim.resume();
        }
      }
    }
  }
};

const selected: AnyObject = {
  columns: [],
  columnTitles: [],

  init: function () {
    for(let i = 0; i < 4; i++) {
      this.columns[i] = document.getElementById(`column${i + 1}`);
      this.columnTitles[i] = document.getElementById(`column${i + 1}_title`);
    }
  },

  remove: function () {
    for(let i = 0; i < 4; i++) {
      this.columns[i]?.style.setProperty('background-color', 'transparent');
      this.columnTitles[i]?.style.setProperty('color', '#303030');
    }
  },

  activeBg: function(index: number) {
    this.columns[index]?.style.setProperty('background-color', 'rgba(141, 141, 163, 0.2)');
  },

  activeTitle: function(index: number) {
    this.columnTitles[index]?.style.setProperty('color', '#000000');
  }
};

const Graph: FC = () => {
  const [started, setStarted] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    const handleWindowResize = () => {
      dot.init('dot', 'curve');
      dot.move(0);

      selected.init();
      anim.start(7000);

      return () => anim.pause();
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  useEffect(() => {
    dot.init('dot', 'curve');
    dot.move(0);

    if(started) {
      selected.init();
      anim.start(7000);

      return () => anim.pause();
    }
  }, [started]);

  const checkState = (isVisible: boolean) => {
    if(isVisible && !started) setStarted(true);
  };

  return (
    <VisibilitySensor onChange={checkState} delayedCall partialVisibility>
      <>
        <GraphContent>
          <GraphColumn id='column1'>
            <GraphRotateText 
              fSize={{ md: 1.313, xxs: 0.875 }} 
              lHeight={1} 
              fWeight={{ md: 700, xxs: 600 }} 
              color='#595959'
            >
              {t('KEY_OUTCOME_METRIC')}
            </GraphRotateText>
            <GraphColumnTitle as='div' id='column1_title'>{t('VISION_AND_MISSION')}</GraphColumnTitle>
          </GraphColumn>
          <GraphColumn id='column2'>
            <GraphColumnTitle as='div' id='column2_title'>{t('MVP')}</GraphColumnTitle>
          </GraphColumn>
          <GraphColumn id='column3'>
            <GraphColumnTitle as='div' id='column3_title'>{t('PRODUCT')}</GraphColumnTitle>
          </GraphColumn>
          <GraphColumn id='column4'>
            <GraphColumnTitle as='div' id='column4_title'>{t('SCALE')}</GraphColumnTitle>
          </GraphColumn>
          <GraphLine id='line'>
            <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 1366 434" fill="none">
              <path id='curve' d="M1.79199 432.212L231.412 411.406H238.301C330.403 411.406 421.161 389.311 502.956 346.975V346.975L524.05 333.85C551.155 316.986 582.44 308.048 614.364 308.048V308.048C630.737 308.048 647.025 310.4 662.729 315.032L673.382 318.174C704.598 327.382 737.635 328.558 769.427 321.593L788.109 317.501C816.144 311.359 841.49 296.436 860.461 274.901V274.901C878.415 254.52 902.103 240.035 928.426 233.339L954.166 226.792C986.893 218.467 1021.23 218.893 1053.74 228.024L1055.52 228.524C1090.58 238.37 1127.74 237.738 1162.44 226.706V226.706C1188.58 218.395 1212.57 204.419 1232.68 185.774L1251.79 168.07C1273.61 147.849 1292.33 124.527 1307.35 98.8519L1364.03 2" stroke="#816CFF" strokeWidth="3" strokeLinecap="round"/>
              <circle
                r="15"
                cy="0"
                cx="0"
                id="dot"
                fill="#64FFE3"
              />
            </svg>
          </GraphLine>
        </GraphContent>
        <Block 
          display='flex' 
          fDirection='row' 
          fAlign='center' 
          mb={{ md: 10.5, xxs: 2.875 }}
          ml={1.563} 
          mt={0.625}
        >
          <GraphLabel width={25}>{t('IDEA')}</GraphLabel>
          <GraphLabel width={50}>{t('STARTUP')}</GraphLabel>
          <GraphLabel width={25}>{t('PRODUCT_GROWTH')}</GraphLabel>
        </Block>
      </>
    </VisibilitySensor>
  );
};

export default Graph;
